import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Copy, ExternalLink, LoaderCircle, Mail, Pencil, Plus, Trash2, Webhook } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import type { Integration } from '@/_shared/types';

type IntegrationType = 'slack_webhook' | 'resend';

const TYPE_LABELS: Record<IntegrationType, { label: string; icon: typeof Webhook }> = {
  slack_webhook: { label: 'Slack Webhook', icon: Webhook },
  resend: { label: 'Resend Email', icon: Mail },
};

const TYPE_SETUP_GUIDES: Record<IntegrationType, { href: string; label: string }> = {
  slack_webhook: {
    href: 'https://docs.slack.dev/messaging/sending-messages-using-incoming-webhooks/',
    label: 'Slack incoming webhook setup',
  },
  resend: {
    href: 'https://resend.com/docs/dashboard/api-keys/introduction',
    label: 'Resend API key setup',
  },
};

// ---------------------------------------------------------------------------
// MCP Connection Section
// ---------------------------------------------------------------------------

function McpConnectionSection(): React.ReactElement {
  const mcpUrl = `${window.location.origin}/mcp`;

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Failed to copy');
    }
  }, []);

  return (
    <section className="mb-8">
      <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">MCP Connection</h2>

      <div className="rounded border border-border bg-card">
        {/* Header */}
        <div className="border-b border-border/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand/10">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-brand">
                <path
                  d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Model Context Protocol (MCP)</p>
              <p className="text-xs text-muted-foreground">
                Connect Claude Desktop, ChatGPT, VS Code, and other MCP clients to your workspace
              </p>
            </div>
          </div>
        </div>

        {/* Setup instructions */}
        <div className="space-y-4 px-4 py-4">
          {/* Server URL */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Server URL</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded border border-border bg-background px-3 py-2 text-sm text-foreground">
                {mcpUrl}
              </code>
              <button
                onClick={() => void copyToClipboard(mcpUrl, 'Server URL')}
                className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2.5 py-2 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand"
              >
                <Copy size={12} />
              </button>
            </div>
          </div>

          {/* Client setup instructions */}
          <div className="rounded-xl border border-border/80 bg-muted/30 px-4 py-3">
            <p className="mb-2 text-xs font-medium text-foreground">Quick Setup</p>
            <ol className="space-y-1.5 text-xs text-muted-foreground">
              <li>
                <span className="mr-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand/10 text-[0.625rem] font-bold text-brand">
                  1
                </span>
                Open your MCP client settings (e.g., Claude Desktop &rarr; Settings &rarr; MCP Servers)
              </li>
              <li>
                <span className="mr-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand/10 text-[0.625rem] font-bold text-brand">
                  2
                </span>
                Add a new server with the URL above &mdash; the client will handle OAuth automatically
              </li>
              <li>
                <span className="mr-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand/10 text-[0.625rem] font-bold text-brand">
                  3
                </span>
                Authorize access when prompted &mdash; you&apos;ll see a consent page with your workspace name
              </li>
            </ol>
          </div>

          {/* Available tools */}
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">Available Tools</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { name: 'Query Warehouse', desc: 'Execute read-only SQL queries with interactive results' },
                { name: 'View Dashboards', desc: 'Browse and explore dashboards with live charts' },
                { name: 'Save Dashboard', desc: 'Create dashboards from conversation (write scope)' },
                { name: 'Source Docs', desc: 'AI-generated instrumentation documentation' },
              ].map((tool) => (
                <div key={tool.name} className="rounded border border-border/60 px-3 py-2">
                  <p className="text-xs font-medium text-foreground">{tool.name}</p>
                  <p className="text-[0.625rem] text-muted-foreground">{tool.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Client support */}
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">Supported Clients</p>
            <div className="flex flex-wrap gap-1.5">
              {['Claude Desktop', 'Claude.ai', 'ChatGPT', 'VS Code', 'Claude Code CLI'].map((client) => (
                <span
                  key={client}
                  className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[0.625rem] text-muted-foreground"
                >
                  {client}
                </span>
              ))}
            </div>
            <p className="mt-1.5 text-[0.625rem] text-muted-foreground/60">
              Interactive charts and tables available in Claude Desktop, Claude.ai, ChatGPT, and VS Code. CLI clients
              receive markdown text summaries.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Coming Soon Integrations
// ---------------------------------------------------------------------------

function ComingSoonSection(): React.ReactElement {
  const comingSoon = [
    {
      name: 'Slack',
      desc: 'Native Slack bot for alerts, reports, and conversational data queries directly in your channels.',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-[#4A154B]">
          <path
            d="M14.5 2a2.5 2.5 0 0 0 0 5H17V4.5A2.5 2.5 0 0 0 14.5 2zM7 7a2.5 2.5 0 0 1 0 5h-2.5a2.5 2.5 0 0 1 0-5zm2.5 7a2.5 2.5 0 0 0-5 0V16.5a2.5 2.5 0 0 0 5 0zm5 2.5a2.5 2.5 0 0 1 5 0v2.5a2.5 2.5 0 0 1-5 0zM17 9.5a2.5 2.5 0 0 0 0 5h2.5a2.5 2.5 0 0 0 0-5zm-7.5 5a2.5 2.5 0 0 0 5 0V12H9.5a2.5 2.5 0 0 0 0 5zm5-12a2.5 2.5 0 0 0 0 5H17V4.5A2.5 2.5 0 0 0 14.5 2z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      ),
    },
    {
      name: 'Linear',
      desc: 'Create and track issues, sync project status, and automate workflows with your Linear workspace.',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-[#5E6AD2]">
          <path
            d="M3.34 17.66l3-3A9 9 0 0 1 3 8.07a9.95 9.95 0 0 0 .34 9.59zm1.66-1.66l3-3a6 6 0 0 1 6.6-6.6l3-3A9.96 9.96 0 0 0 12 2C6.48 2 2 6.48 2 12c0 1.52.34 2.96.95 4.25z"
            fill="currentColor"
          />
        </svg>
      ),
    },
  ];

  return (
    <section className="mb-8">
      <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Coming Soon</h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {comingSoon.map((item) => (
          <div
            key={item.name}
            className="flex items-start gap-3 rounded border border-dashed border-border bg-card/50 px-4 py-3 opacity-70"
          >
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
              {item.icon}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground">{item.name}</p>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[0.5625rem] font-semibold tracking-wider text-muted-foreground uppercase">
                  Coming Soon
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Workflow Integrations (existing)
// ---------------------------------------------------------------------------

export function SettingsIntegrations(): React.ReactElement {
  usePageTitle('Integrations — Settings');
  const queryClient = useQueryClient();
  const { isAdmin } = useWorkspace();
  const orpc = useOrpc();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newType, setNewType] = useState<IntegrationType>('slack_webhook');

  const { data, isLoading } = useQuery(orpc.settings.integrations.list.queryOptions({ input: {} }));
  const integrations = (data?.integrations ?? []) as Integration[];

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.settings.integrations.list.queryOptions({ input: {} }).queryKey,
    });
  };

  const saveMutation = useMutation(
    orpc.settings.integrations.save.mutationOptions({
      onSuccess: () => {
        invalidate();
        setEditingId(null);
        setShowNew(false);
        toast.success('Integration saved');
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to save integration');
      },
    }),
  );

  const deleteMutation = useMutation(
    orpc.settings.integrations.delete.mutationOptions({
      onSuccess: () => {
        invalidate();
        toast.success('Integration removed');
      },
      onError: () => {
        toast.error('Failed to remove integration');
      },
    }),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <LoaderCircle size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <>
      <McpConnectionSection />
      <ComingSoonSection />

      {!isAdmin && (
        <div className="mb-4 rounded border border-amber-200/50 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Only workspace admins can modify integrations.
        </div>
      )}

      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">Workflow Integrations</h2>
        {isAdmin && !showNew && (
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20"
          >
            <Plus size={12} />
            Add Integration
          </button>
        )}
      </div>

      {/* New integration form */}
      {showNew && (
        <section className="mb-4 rounded border border-brand/30 bg-card p-4">
          <div className="mb-3 flex items-center gap-3">
            <label className="text-xs text-muted-foreground">Type</label>
            <div className="flex gap-1">
              {(Object.entries(TYPE_LABELS) as [IntegrationType, (typeof TYPE_LABELS)[IntegrationType]][]).map(
                ([type, { label, icon: Icon }]) => (
                  <button
                    key={type}
                    onClick={() => setNewType(type)}
                    className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs transition-colors ${
                      newType === type
                        ? 'border border-brand bg-brand/10 text-brand'
                        : 'border border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon size={12} />
                    {label}
                  </button>
                ),
              )}
            </div>
          </div>
          <IntegrationForm
            type={newType}
            disabled={!isAdmin}
            saving={saveMutation.isPending}
            onSave={(name, config) => saveMutation.mutate({ name, type: newType, config })}
            onCancel={() => setShowNew(false)}
          />
        </section>
      )}

      {/* List */}
      {integrations.length === 0 && !showNew && (
        <section className="rounded border border-border bg-card p-8 text-center">
          <Webhook size={32} className="mx-auto mb-3 text-muted-foreground/40" />
          <p className="mb-1 text-sm text-muted-foreground">No integrations configured</p>
          <p className="text-xs text-muted-foreground/60">
            Add a Slack webhook or Resend email integration to use with workflows.
          </p>
        </section>
      )}

      <div className="space-y-2">
        {integrations.map((integration) => {
          const { label, icon: Icon } = TYPE_LABELS[integration.type];
          const isEditing = editingId === integration.id;

          return (
            <section key={integration.id} className="rounded border border-border bg-card">
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="h-2 w-2 shrink-0 rounded-full bg-green-500" />
                <Icon size={14} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{integration.name}</span>
                <span className="shrink-0 rounded border border-border bg-background px-2 py-0.5 text-[0.625rem] font-semibold tracking-wider text-muted-foreground uppercase">
                  {label}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground/60">
                  {integration.configHint.webhookUrl ?? integration.configHint.fromEmail ?? ''}
                </span>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingId(isEditing ? null : integration.id)}
                      className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand"
                    >
                      {isEditing ? (
                        <ChevronDown size={12} className="rotate-180 transition-transform" />
                      ) : (
                        <Pencil size={11} />
                      )}
                      {isEditing ? 'Close' : 'Edit'}
                    </button>
                    <button
                      onClick={() => deleteMutation.mutate({ id: integration.id })}
                      disabled={deleteMutation.isPending}
                      className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-700"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                )}
              </div>

              {isEditing && (
                <div className="border-t border-border px-4 pt-3 pb-4">
                  <IntegrationForm
                    type={integration.type}
                    initialName={integration.name}
                    disabled={!isAdmin}
                    saving={saveMutation.isPending}
                    onSave={(name, config) =>
                      saveMutation.mutate({ id: integration.id, name, type: integration.type, config })
                    }
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}

function IntegrationForm({
  type,
  initialName,
  disabled,
  saving,
  onSave,
  onCancel,
}: {
  type: IntegrationType;
  initialName?: string;
  disabled: boolean;
  saving: boolean;
  onSave: (name: string, config: string) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [name, setName] = useState(initialName ?? '');
  const setupGuide = TYPE_SETUP_GUIDES[type];
  // Slack fields
  const [webhookUrl, setWebhookUrl] = useState('');
  // Resend fields
  const [apiKey, setApiKey] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');

  const handleSubmit = () => {
    let config: string;
    if (type === 'slack_webhook') {
      config = JSON.stringify({ webhookUrl });
    } else {
      config = JSON.stringify({
        apiKey,
        fromEmail,
        ...(fromName ? { fromName } : {}),
      });
    }
    onSave(name, config);
  };

  const isValid =
    name.trim().length > 0 &&
    (type === 'slack_webhook'
      ? webhookUrl.startsWith('https://')
      : apiKey.trim().length > 0 && fromEmail.includes('@'));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-muted/30 px-3 py-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-foreground">Need help setting this up?</p>
          <p className="text-xs text-muted-foreground">
            Open the official {TYPE_LABELS[type].label} guide in a new tab.
          </p>
        </div>
        <a
          href={setupGuide.href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-brand/20 bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand transition-colors hover:bg-brand/15"
        >
          <span>{setupGuide.label}</span>
          <ExternalLink size={12} />
        </a>
      </div>

      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={type === 'slack_webhook' ? 'e.g. #sales-alerts' : 'e.g. Transactional Email'}
          disabled={disabled}
          className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-brand disabled:opacity-50"
        />
      </div>

      {type === 'slack_webhook' ? (
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Webhook URL</label>
          <input
            type="password"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://hooks.slack.com/services/..."
            disabled={disabled}
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-brand disabled:opacity-50"
          />
          <p className="mt-1 text-xs text-muted-foreground">Create at Slack &rarr; Your App &rarr; Incoming Webhooks</p>
        </div>
      ) : (
        <>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="re_..."
              disabled={disabled}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-brand disabled:opacity-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">From Email</label>
            <input
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="alerts@yourdomain.com"
              disabled={disabled}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-brand disabled:opacity-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">
              From Name <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <input
              type="text"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Rudder AI"
              disabled={disabled}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-brand disabled:opacity-50"
            />
          </div>
        </>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={handleSubmit}
          disabled={disabled || saving || !isValid}
          className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
        >
          {saving && <LoaderCircle size={12} className="animate-spin" />}
          Save
        </button>
        <button
          onClick={onCancel}
          className="rounded border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
